import { useNavigate } from "react-router-dom";
import { Icon, Skeleton, Tooltip, Typography } from "@/components/atoms";
import { usePricingProfileContext, useStateRatesContext } from "@/components/organisms";
import { Restricted } from "@/components/pages/Restricted/Restricted";
import { FixedPricingState } from "@/redux/slices/trip/types";
import { clsx } from "@/utils";
import { StateArtwork } from "../stateArtwork";
import { StateRatesEdit } from "./StateRatesEdit";
import { StateRatesView } from "./StateRatesView";

export const StateRates = () => {
  const navigate = useNavigate();
  const { isFetching: isFetchingPricingProfile, isRestricted, pricingProfile } = usePricingProfileContext();
  const { isFetching: isFetchingStateRates, states } = useStateRatesContext();
  const isFetching = isFetchingPricingProfile || isFetchingStateRates;

  if (isFetching) {
    return (
      <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col px-5 py-4">
        <Skeleton />
      </div>
    );
  }

  if (isRestricted) {
    return (
      <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col px-5 py-4">
        <Restricted />
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col px-5 py-4">
      <div className="flex flex-col justify-between gap-4 xl:flex-row">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">{`${pricingProfile ? `${pricingProfile.name}: ` : ""}State Rates`}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Pick a state to set prices by distance range. If no prices are set, the system will apply Custom Rates, then fall back to
            Default Rates.
          </Typography>
        </div>
      </div>
      <div className="mt-4 flex flex-col items-center gap-5 xl:flex-row xl:items-stretch">
        <ul role="list" className="mt-5 grid w-full grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-5">
          {states.map((state) => {
            const { managed, baseInterval, state: slug, id } = state;
            const isDefault = !managed && !baseInterval;
            return (
              <li
                key={id}
                className={clsx(
                  "flex min-w-0 flex-col gap-1 rounded-lg border border-neutral-mid-gray bg-white p-2.5",
                  managed ? "cursor-default" : "cursor-pointer"
                )}
                onClick={() => (managed ? null : navigate(slug))}
              >
                <StateDisplay state={state} />
                <Typography variant="action" className="text-neutral-dark-gray">
                  {state.name}
                </Typography>
                {!isDefault && (
                  <div className="flex flex-row items-center gap-x-1.5 text-neutral-dark-gray">
                    <Icon size="md" name="Setting5" className="flex-shrink-0" />
                    <Typography variant="paragraph">Using Contract Pricing</Typography>
                  </div>
                )}
                {managed ? (
                  <div className="flex">
                    <div>
                      <Tooltip content="Pricing is managed by RideMinder. Please contact RideMinder to make changes" placement="top">
                        <Icon name="Lock1" size="sm" />
                      </Tooltip>
                    </div>
                  </div>
                ) : (
                  <Typography variant="action" className="text-info">
                    {isDefault ? "Set Custom Prices" : "Edit Custom Prices"}
                  </Typography>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

interface StateDisplayProps {
  state: FixedPricingState;
}

const StateDisplay = ({ state }: StateDisplayProps) => {
  const { managed, baseInterval, id } = state;
  const isDefault = !managed && !baseInterval;

  const className = clsx(
    "block w-full h-auto min-h-[150px] shrink-0",
    managed || isDefault
      ? "[--state-fill-color:theme(colors.primary.light)] [--state-stroke-color:theme(colors.primary.DEFAULT)]"
      : "[--state-fill-color:theme(colors.orange.light)] [--state-stroke-color:theme(colors.orange.DEFAULT)]"
  );

  return <StateArtwork slug={id} className={className} fallbackClassName={clsx(className, "rounded-md bg-neutral-gray")} />;
};

StateRates.View = StateRatesView;
StateRates.Edit = StateRatesEdit;
